import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../ui';
import { cx } from '../ui/cx';
import { iconFor } from '../shell/icons';
import OnboardingAction from './OnboardingActions';
import styles from './Onboarding.module.css';

/** Rundgang als Folge von Karten – wischen, Pfeiltasten oder Knöpfe */
export default function OnboardingTour({ open, steps, onFinish }) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef(null);

  useEffect(() => { if (open) setIndex(0); }, [open]);

  const step = steps[index];
  if (!step) return null;
  const Icon = iconFor(step.icon);
  const isLast = index === steps.length - 1;

  const next = () => (isLast ? onFinish() : setIndex((i) => Math.min(i + 1, steps.length - 1)));
  const back = () => setIndex((i) => Math.max(i - 1, 0));

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); back(); }
  };

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (dx < -50) next();
    else if (dx > 50) back();
  };

  return (
    <Dialog open={open} onClose={onFinish} className={styles.root}>
      <DialogBackdrop transition className={styles.backdrop} />
      <div className={styles.viewport}>
        <DialogPanel
          transition
          className={styles.panel}
          onKeyDown={onKeyDown}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className={styles.top}>
            <span className={styles.counter}>{index + 1} / {steps.length}</span>
            {!isLast && <button type="button" className={styles.skip} onClick={onFinish}>Überspringen</button>}
          </div>

          <div key={step.id} className={styles.slide} aria-live="polite">
            <div className={styles.illustration}><Icon aria-hidden /></div>
            <DialogTitle className={styles.title}>{step.title}</DialogTitle>
            <p className={styles.text}>{step.text}</p>
            {step.action && <OnboardingAction action={step.action} />}
          </div>

          <div className={styles.dots} aria-hidden>
            {steps.map((s, i) => <span key={s.id} className={cx(styles.dot, i === index && styles.dotActive)} />)}
          </div>

          <div className={styles.actions}>
            {index > 0 ? (
              <Button variant="ghost" icon={ArrowLeft} onClick={back}>Zurück</Button>
            ) : <span />}
            <Button iconRight={isLast ? undefined : ArrowRight} onClick={next} data-autofocus>
              {isLast ? 'Los geht’s' : 'Weiter'}
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
